import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api.js";
import { useAsync } from "../lib/hooks.js";
import { toScheduleBody } from "../lib/schedule.js";
import {
  Callout,
  Card,
  Empty,
  ErrorState,
  Field,
  Loading,
  PageIntro,
  TransactionBadge,
  formatRelative,
  useToast,
} from "../components/ui.jsx";
import { IconCommand, IconWarning } from "../components/icons.jsx";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Commands() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const deviceNumber = params.get("device") ?? "";

  const catalog = useAsync(() => api.commandCatalog(), []);
  const devices = useAsync(() => api.listDevices({ state: "ACTIVE", limit: 500 }), []);

  const [code, setCode] = useState("");
  const [channel, setChannel] = useState(0);
  const [value, setValue] = useState("");
  const [text, setText] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [schedule, setSchedule] = useState(defaultSchedule());
  const [busy, setBusy] = useState(false);

  const device = (devices.data ?? []).find((d) => d.Number === deviceNumber);
  const spec = (catalog.data ?? []).find((s) => s.Code === code);

  // Only commands the selected device can actually accept are offered. Showing
  // the rest and failing on submit teaches operators to ignore errors.
  const available = useMemo(() => {
    const all = catalog.data ?? [];
    if (!device) return all;
    const isGateway = device.Kind === "NOVALINK";
    return all.filter((s) => {
      if (s.Target === 1) return isGateway;
      if (s.Target === 0) return !isGateway;
      return true;
    });
  }, [catalog.data, device]);

  const history = useAsync(
    () => (deviceNumber ? api.deviceCommands(deviceNumber, 25) : Promise.resolve([])),
    [deviceNumber]
  );

  function selectDevice(number) {
    setParams(number ? { device: number } : {});
    setCode("");
    setChannel(0);
    setConfirm(false);
  }

  async function send(event) {
    event.preventDefault();
    if (!device || !spec) return;
    setBusy(true);
    try {
      const body = { code: spec.Code, channel: Number(channel) || 0, confirm };
      if (spec.Param === 1 || spec.Param === 2 || spec.Param === 3) {
        body.value = Number(value);
      }
      if (spec.Param === 4) body.text = text;
      if (spec.Param === 5) body.schedule = toScheduleBody(schedule);

      const tx = await api.sendCommand(device.Number, body);
      toast.success(
        `${spec.Name} sent`,
        `Transaction ${tx.ID}. It stays open until the device acknowledges it.`
      );
      setConfirm(false);
      setValue("");
      setText("");
      history.reload();
    } catch (err) {
      toast.error("The command was not sent", err.message);
    } finally {
      setBusy(false);
    }
  }

  if (catalog.loading || devices.loading) return <Loading label="Loading the command catalogue" />;
  if (catalog.error) return <ErrorState error={catalog.error} onRetry={catalog.reload} />;

  return (
    <>
      <PageIntro title="Send a command to a device">
        Commands travel to the device through its gateway and are tracked until it
        acknowledges them. Only devices in service can accept one. Anything that
        interrupts supply or erases state asks for explicit confirmation first.
      </PageIntro>

      <div className="grid grid-2">
        <Card title="New command" hint="Step through the fields in order">
          <form onSubmit={send}>
            <Field
              label="Device"
              required
              hint="Only devices currently in service are listed."
            >
              <select
                className="select"
                value={deviceNumber}
                onChange={(e) => selectDevice(e.target.value)}
                required
              >
                <option value="">Choose a device</option>
                {(devices.data ?? []).map((d) => (
                  <option key={d.Number} value={d.Number}>
                    {d.Number} · {d.Kind === "NOVALINK" ? "NovaLink gateway" : d.ModelCode}
                  </option>
                ))}
              </select>
            </Field>

            {device && (
              <>
                <Field
                  label="Command"
                  required
                  hint={
                    spec
                      ? spec.Destructive
                        ? "This command interrupts supply or erases state."
                        : "Safe to send at any time."
                      : "Only commands this device kind accepts are shown."
                  }
                >
                  <select
                    className="select"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value);
                      setConfirm(false);
                      setValue("");
                    }}
                    required
                  >
                    <option value="">Choose a command</option>
                    {available.map((s) => (
                      <option key={s.Code} value={s.Code}>
                        {s.Name} ({s.Code})
                      </option>
                    ))}
                  </select>
                </Field>

                {spec?.RequiresChannel && device.ChannelCount > 1 && (
                  <Field
                    label="Channel"
                    hint="Leave as the whole unit to affect every circuit at once."
                  >
                    <select
                      className="select"
                      value={channel}
                      onChange={(e) => setChannel(e.target.value)}
                    >
                      <option value={0}>Whole unit</option>
                      {Array.from({ length: device.ChannelCount }, (_, i) => i + 1).map((i) => (
                        <option key={i} value={i}>
                          Channel {i}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}

                {spec && [1, 2, 3].includes(spec.Param) && (
                  <Field
                    label={paramLabel(spec)}
                    required
                    hint={
                      spec.Max > 0
                        ? `Between ${spec.Min} and ${spec.Max}.`
                        : "A whole number."
                    }
                  >
                    <input
                      className="input"
                      type="number"
                      value={value}
                      min={spec.Min}
                      max={spec.Max > 0 ? spec.Max : undefined}
                      step={spec.Param === 1 ? "any" : 1}
                      onChange={(e) => setValue(e.target.value)}
                      required
                    />
                  </Field>
                )}

                {spec?.Param === 4 && (
                  <Field label="Value" required hint="Plain text. The framing character $ is not allowed.">
                    <input
                      className="input mono"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      maxLength={spec.Max || undefined}
                      required
                    />
                  </Field>
                )}

                {spec?.Param === 5 && (
                  <ScheduleFields schedule={schedule} onChange={setSchedule} code={spec.Code} />
                )}

                {spec?.Destructive && (
                  <label className="checkbox danger" style={{ marginBottom: 16 }}>
                    <input
                      type="checkbox"
                      checked={confirm}
                      onChange={(e) => setConfirm(e.target.checked)}
                    />
                    <span>
                      <span className="checkbox-label">
                        I understand this will {destructiveEffect(spec.Code)}
                      </span>
                      <span className="checkbox-hint">
                        The server refuses this command without an explicit confirmation.
                      </span>
                    </span>
                  </label>
                )}

                <button
                  className="btn btn-primary btn-block"
                  disabled={busy || !spec || (spec.Destructive && !confirm)}
                >
                  {busy ? <span className="spinner" /> : <IconCommand />}
                  {busy ? "Sending" : `Send ${spec?.Name ?? "command"}`}
                </button>
              </>
            )}
          </form>
        </Card>

        <Card
          title="Recent commands"
          hint={deviceNumber ? `For ${deviceNumber}` : "Choose a device to see its history"}
        >
          {!deviceNumber ? (
            <Empty title="No device selected">
              Pick a device on the left and its recent commands will appear here.
            </Empty>
          ) : history.loading ? (
            <Loading />
          ) : (history.data ?? []).length === 0 ? (
            <Empty title="Nothing sent yet">
              This device has not been sent a command from the console.
            </Empty>
          ) : (
            <div className="table-wrap" style={{ border: "none" }}>
              <table>
                <thead>
                  <tr>
                    <th>Command</th>
                    <th>State</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.map((t) => (
                    <tr key={t.ID}>
                      <td>
                        <span className="mono">{t.CommandType}</span>
                        {t.ChannelIndex > 0 && <span className="hint"> ch{t.ChannelIndex}</span>}
                      </td>
                      <td>
                        <TransactionBadge state={t.State} />
                      </td>
                      <td className="hint">{formatRelative(t.CreatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Callout kind="info">
            A command that stays <strong>Dispatched</strong> has not been acknowledged. It is
            swept to <strong>Expired</strong> once its deadline passes, so an unacknowledged
            command is never mistaken for one still in flight.
          </Callout>
        </Card>
      </div>
    </>
  );
}

function ScheduleFields({ schedule, onChange, code }) {
  const set = (patch) => onChange({ ...schedule, ...patch });

  return (
    <div style={{ padding: 14, background: "var(--surface-2)", borderRadius: "var(--radius)", marginBottom: 16 }}>
      <Field label="Days of the week" required hint="The window applies only on the selected days.">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {DAYS.map((day, i) => (
            <button
              type="button"
              key={day}
              className={`btn btn-sm ${schedule.days[i] ? "btn-primary" : "btn-secondary"}`}
              onClick={() => {
                const days = [...schedule.days];
                days[i] = !days[i];
                set({ days });
              }}
            >
              {day}
            </button>
          ))}
        </div>
      </Field>

      <div className="field-row">
        <Field label="Start time" required>
          <input
            className="input"
            type="time"
            value={schedule.startTime}
            onChange={(e) => set({ startTime: e.target.value })}
            required
          />
        </Field>
        <Field label="End time" required>
          <input
            className="input"
            type="time"
            value={schedule.endTime}
            onChange={(e) => set({ endTime: e.target.value })}
            required
          />
        </Field>
      </div>

      <div className="field-row">
        <Field label="Start date" required>
          <input
            className="input"
            type="date"
            value={schedule.startDate}
            onChange={(e) => set({ startDate: e.target.value })}
            required
          />
        </Field>
        <Field label="End date" required>
          <input
            className="input"
            type="date"
            value={schedule.endDate}
            onChange={(e) => set({ endDate: e.target.value })}
            required
          />
        </Field>
      </div>

      <Field
        label={code === "ENB" ? "Wattage cap (W)" : "Energy allowance (kWh)"}
        required
      >
        <input
          className="input"
          type="number"
          min="0"
          step={code === "ENB" ? 1 : "0.1"}
          value={schedule.limit}
          onChange={(e) => set({ limit: e.target.value })}
          required
        />
      </Field>
    </div>
  );
}

function defaultSchedule() {
  const today = new Date().toISOString().slice(0, 10);
  const year = new Date();
  year.setFullYear(year.getFullYear() + 1);
  return {
    days: [false, true, true, true, true, true, false],
    startTime: "06:00",
    endTime: "22:00",
    startDate: today,
    endDate: year.toISOString().slice(0, 10),
    limit: "10",
  };
}

/** Converts the form's friendly values into the wire representation. */

function paramLabel(spec) {
  switch (spec.Param) {
    case 1:
      return "Power (watts)";
    case 2:
      return "Duration (seconds)";
    case 3:
      return "Amount";
    default:
      return "Value";
  }
}

function destructiveEffect(code) {
  return (
    {
      DIS: "cut supply to the load",
      REM: "remove credit from the device",
      CLR: "erase the device's remaining credit",
      DMC: "clear the gateway's device membership",
      RST: "restart the device",
      FWC: "commit staged firmware",
      FWR: "roll the firmware back",
    }[code] ?? "change the device irreversibly"
  );
}
